/**
 * One shared idea of "now", taken from the server.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT `Date.now()`
 * ---------------------------------------------------------------------------
 * Because two devices side by side do not agree on it. Phones resync over the
 * network and are usually within a second; desktops that have been asleep, or
 * have NTP switched off, or are dual-booting with a BIOS clock in local time,
 * are routinely minutes out. Render the countdown against the local clock and
 * two devices showing *identical, correctly synced state* display different
 * numbers - the one failure this whole site is built to avoid.
 *
 * Every `/api/state` response carries the server's clock, so the fix is free:
 * one authority, whatever each device happens to believe.
 *
 * ---------------------------------------------------------------------------
 * WHY THE LOWEST ROUND TRIP WINS
 * ---------------------------------------------------------------------------
 * The naive `offset = serverNow - Date.now()` charges the entire network round
 * trip to the clock, so a 400 ms mobile request makes the device look 400 ms
 * slow. Splitting the trip in half is the standard correction and is exact only
 * if the two legs are symmetric - which they roughly are, and badly are not on
 * a congested link.
 *
 * So samples are not averaged. Averaging lets one 3-second stall on a train
 * drag the estimate around for the next ten polls. The lowest-latency sample
 * seen is kept instead, on the reasoning that a fast round trip has the least
 * room to hide asymmetry, and it is retired after `SAMPLE_TTL_MS` so a genuinely
 * drifting device is not pinned to a stale reading forever.
 */

interface Sample {
  offset: number
  rtt: number
  takenAt: number
}

const SAMPLE_TTL_MS = 10 * 60_000

let best: Sample | null = null

/**
 * Feed in a completed request. `sentAt` and `receivedAt` are local
 * `Date.now()` readings from either side of the fetch.
 */
export function adoptSample(serverNow: number, sentAt: number, receivedAt: number): void {
  if (!Number.isFinite(serverNow)) return
  const rtt = Math.max(0, receivedAt - sentAt)
  const offset = serverNow + rtt / 2 - receivedAt
  const expired = best !== null && receivedAt - best.takenAt > SAMPLE_TTL_MS
  if (best === null || expired || rtt <= best.rtt) {
    best = { offset, rtt, takenAt: receivedAt }
  }
}

/** Epoch milliseconds, corrected towards the server. */
export function serverNow(): number {
  return Date.now() + (best ? best.offset : 0)
}

/** True once a real sample has been taken. Until then `serverNow` is local time. */
export function isSynced(): boolean {
  return best !== null
}
